/**
 * The portal systems the interaction layer borrows whole.
 *
 * The order domain itself lives in `@hopdrive/feature-order-index`; what stays here is the
 * dealer-only half — its chat and its move-details fetch — because each is an established portal
 * flow with its own backend contract. This file is the whole list, so an app wiring its own
 * interaction layer knows exactly what it has to supply.
 */

import { useMemo } from 'react';
import { useChatModal } from '@components/Chat/ChatModalManager';
import { useGetMoveDetailsQuery } from '@features/moves/queries/getMoveDetails.query.generated';
import type { Moves, Offers } from '@gql/base-types';
import { findOffersInScenarios } from '@services/moveService';

/**
 * The portal's mutating dialogs are no longer in this list.
 *
 * All four — `MoveCancelModal`, `CancelLiteModal`, `MoveTimeModal` and now
 * `AssignOrUnassignMoveModal` — live in `@hopdrive/feature-move-actions`, where the
 * embed runs the portal's version instead of its own older fork, and the provider imports them
 * from the package directly. What an app still supplies to the assign dialog — its dispatch
 * service, its session, its rideshare grant — is `@components/Modals/assignModalHost`.
 */

/**
 * The portal's guest-SMS chat, opened for one move.
 *
 * **Guest chat is per-move by nature, not order-shaped**, and the machinery around it
 * already works — a modal mounted once in the app layout, its own message queries and
 * subscription, and a localStorage read-status tracker behind the unread dot. So this
 * feature rebuilds none of it. Its whole contribution is deciding *which* move to open:
 * the order's guest-facing leg.
 *
 * The provider lives in the app layout, so any surface inside it can call this. A
 * consumer of this feature outside that layout has to supply its own — which is exactly
 * what the seam is for.
 */
export const usePortalGuestChat = (): {
  openChat: ((moveId: number, isGuestAtPickup?: boolean) => void) | null;
} => {
  // Null-safe on purpose: the chat context throws where its provider is not mounted, and
  // a scenario surface outside the app layout should lose the chat action, not crash.
  // The hook is still called unconditionally, so the rules of hooks hold.
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks -- always called; the try only catches the missing-provider throw
    const { openChat } = useChatModal();
    return { openChat };
  } catch {
    return { openChat: null };
  }
};

/**
 * The subject move for the portal's action dialogs, fetched **only when one opens**.
 *
 * Every one of those dialogs takes a whole `Moves` object and resolves the rest itself —
 * the paired leg through `parentMove` / `childMoves`, the live offers through the scenario
 * links. None of that is on the dashboard's page query, and putting it there would make
 * every card on every page carry relation trees for actions almost nobody clicks.
 *
 * So this reuses the move-details query, which is by definition exactly what those dialogs
 * were built against. One round trip, once, on open.
 */
export const usePortalMoveForActions = (moveId: number | null) => {
  const { data, loading } = useGetMoveDetailsQuery({
    variables: { moveId: moveId ?? 0 },
    skip: moveId === null,
    fetchPolicy: 'cache-and-network',
  });

  // The generated result type is the query's selection set, while the dialogs are typed
  // against the whole `Moves` row. The portal's own move-details page asserts the same way,
  // and the seam is where a portal type adaptation belongs rather than at a call site.
  const move = (data?.moves_by_pk ?? null) as Moves | null;
  const offers = useMemo(() => (findOffersInScenarios(move?.scenariotomoves ?? []) ?? []) as Offers[], [move]);

  return { move, offers, loading };
};
